import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Spinner,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";

//i18n
import { withTranslation } from "react-i18next";

import withRouter from "../../../components/Common/withRouter";
import SwitchContextConfirmModal from "../SwitchContextConfirmModal";
import {
  hydrateActiveContext,
  switchContext,
  refreshActiveContext,
} from "../../../store/activeContext/actions";

/**
 * Topbar dropdown showing the user's avatar and the org switcher.
 *
 * <p>The switcher lists every organization the user is a member of (from
 * {@code state.ActiveContext.memberships}) and — when the user has
 * {@code org:aggregate-view} permission in two or more of them — adds a
 * "Global View" entry at the top. Selecting an org issues a fresh JWT scoped
 * to that org and navigates to {@code /dashboard}; selecting Global View
 * issues a JWT with {@code activeOrganizationId = null} and navigates to
 * {@code /app/global/dashboard}.
 */
const ProfileMenu = (props) => {
  const [menu, setMenu] = useState(false);
  // Pending switch target — null means no confirmation modal is open. Holds
  // {organizationId, name} so the modal can name the target and the confirm
  // button knows what to dispatch.
  const [pendingSwitch, setPendingSwitch] = useState(null);
  const dispatch = useDispatch();
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const { profileUrl, username } = authUser;
  const userName = username || "User";

  const activeContext = useSelector((s) => s.ActiveContext);
  const {
    activeOrganizationId,
    activeOrganizationName,
    isGlobalView,
    memberships = [],
    canEnterGlobalView = false,
    switching,
  } = activeContext;

  // Hydrate the slice from localStorage on mount; the action is idempotent.
  // The slice already self-bootstraps from storage on module load, so this
  // is a belt-and-braces refresh after re-login or external mutations.
  useEffect(() => {
    dispatch(hydrateActiveContext());
  }, [dispatch]);

  // Single source of truth for the Global View entry's visibility — set by
  // the backend on login and on every switch-context response.
  const showGlobalView = canEnterGlobalView;
  // Display-only: how many of the user's orgs feed Global View. Visibility
  // is gated by canEnterGlobalView above, not by this count.
  const aggregateOrgCount = memberships.filter((m) => m && m.hasAggregateView).length;

  const triggerLabel = isGlobalView
    ? props.t("shared.global_view.label", "Global View")
    : (activeOrganizationName || "");

  // Click on an org / Global View — open the confirmation modal instead of
  // dispatching immediately. The actual dispatch happens in confirmSwitch.
  const requestSwitch = (organizationId, name) => {
    if (switching) return;
    setMenu(false);
    setPendingSwitch({ organizationId, name });
  };

  const cancelSwitch = () => {
    if (switching) return;
    setPendingSwitch(null);
  };

  const confirmSwitch = () => {
    if (!pendingSwitch) return;
    const { organizationId } = pendingSwitch;
    dispatch(
      switchContext(organizationId, (targetPath) => {
        setPendingSwitch(null);
        // Hard reload to drop every page's in-memory state (mounted lists,
        // dropdown caches, permission hook results). SPA navigation here was
        // leaving stale dropdowns / data from the previous org visible until
        // the user manually refreshed, which is a security issue with
        // multi-org accounts.
        window.location.href = targetPath;
      })
    );
  };

  const globalViewLabel = props.t("shared.global_view.label", "Global View");

  return (
    <React.Fragment>
      <Dropdown
        isOpen={menu}
        toggle={() => {
          // Refresh the envelope the moment the user opens the menu so the
          // membership list reflects any server-side grants/revokes that
          // happened mid-session.
          if (!menu) dispatch(refreshActiveContext());
          setMenu(!menu);
        }}
        className="d-inline-block"
      >
        <DropdownToggle
          className="btn header-item user text-start d-flex align-items-center"
          id="page-header-user-dropdown"
          tag="button"
          data-bs-toggle="dropdown"
          aria-haspopup={true}
          aria-expanded={menu}
        >
          <img
            className="rounded-circle header-profile-user"
            src={profileUrl}
            alt="Header Avatar"
          />
          <span className="d-none d-xl-inline-block ms-2">
            <span className="d-block fw-medium font-size-14">{userName}</span>
            <span className="d-block text-muted font-size-12">{triggerLabel}</span>
          </span>
        </DropdownToggle>
        <DropdownMenu className="dropdown-menu-end pt-0" style={{ minWidth: 280 }}>
          {showGlobalView && (
            <>
              <DropdownItem
                onClick={() => requestSwitch(null, globalViewLabel)}
                disabled={switching || isGlobalView}
                active={isGlobalView}
              >
                <i className="bx bx-globe text-muted font-size-18 align-middle me-2" />
                <span className="align-middle">{globalViewLabel}</span>
                <span className="text-muted ms-2 font-size-12">
                  ({aggregateOrgCount} {props.t("shared.global_view.orgs_short", "orgs")})
                </span>
                {switching && isGlobalView && (
                  <Spinner size="sm" className="ms-2" />
                )}
              </DropdownItem>
              <div className="dropdown-divider"></div>
            </>
          )}

          {memberships.length > 0 && (
            <>
              <DropdownItem header className="text-uppercase text-muted font-size-11">
                {props.t("shared.org_switcher.section_header", "Organizations")}
              </DropdownItem>
              {memberships.map((m) => {
                const isActive = !isGlobalView && activeOrganizationId === m.organizationId;
                return (
                  <DropdownItem
                    key={`org-${m.organizationId}`}
                    onClick={() => requestSwitch(m.organizationId, m.organizationName)}
                    disabled={switching || isActive}
                    active={isActive}
                  >
                    <i className="bx bx-buildings text-muted font-size-18 align-middle me-2" />
                    <span className="align-middle">{m.organizationName}</span>
                    {switching && isActive && (
                      <Spinner size="sm" className="ms-2" />
                    )}
                  </DropdownItem>
                );
              })}
              <div className="dropdown-divider"></div>
            </>
          )}

          <DropdownItem tag="a" href="/logout">
            <i className="bx bx-log-out text-muted font-size-18 align-middle me-1" />
            <span className="align-middle">{props.t("shared.logout", "Logout")}</span>
          </DropdownItem>
        </DropdownMenu>
      </Dropdown>
      <SwitchContextConfirmModal
        isOpen={!!pendingSwitch}
        targetName={pendingSwitch?.name}
        switching={switching}
        onConfirm={confirmSwitch}
        onCancel={cancelSwitch}
      />
    </React.Fragment>
  );
};

ProfileMenu.propTypes = {
  success: PropTypes.any,
  t: PropTypes.any,
  router: PropTypes.any,
};

export default withRouter(withTranslation()(ProfileMenu));
